/**
 * Étape 12 — Math helpers pour l'édition de tables en vue « Toutes ».
 *
 * Coordonnées :
 * - `local` : repère interne à une zone (origine = top-left du rectangle non rotaté de la zone).
 * - `global` : repère du canvas Toutes (origine = top-left du canvas global, après le padding `offX/offY`
 *   appliqué dans `FloorPlanV2.tsx`). Les helpers ici travaillent SANS le padding — l'appelant ajoute/retire
 *   `offX/offY` à la marge.
 *
 * Une zone est positionnée à `(zone.posX, zone.posY)` dans le repère global et tournée de `zone.rotation`
 * degrés (∈ {0, 90, 180, 270}) autour de son coin top-left.
 */

export type ZoneRot = 0 | 90 | 180 | 270;

export interface AllViewZoneLite {
  id: string;
  posX: number;
  posY: number;
  canvasWidth: number;
  canvasHeight: number;
  rotation: ZoneRot;
  /** Ordre d'empilage : la plus grande gagne quand plusieurs zones contiennent le point. */
  orderIndex: number;
}

const rad = (deg: number) => (deg * Math.PI) / 180;

/**
 * Convertit un point zone-local `(lx, ly)` vers le repère global (hors padding).
 *
 * Le wrapper visuel de la zone est `transform: rotate(rotation) translate(posX, posY)` avec
 * `transformOrigin: top-left`. En réalité dans le DOM on a `left/top` PUIS `transform: rotate(...)`,
 * donc l'ordre effectif est : `translate(posX, posY)` PUIS `rotate(rotation)` autour de `(posX, posY)`.
 */
export function globalFromLocal(zone: AllViewZoneLite, lx: number, ly: number) {
  const cos = Math.cos(rad(zone.rotation));
  const sin = Math.sin(rad(zone.rotation));
  return {
    x: zone.posX + lx * cos - ly * sin,
    y: zone.posY + lx * sin + ly * cos,
  };
}

/**
 * Convertit un point global `(gx, gy)` vers le repère local d'une zone.
 */
export function localFromGlobal(zone: AllViewZoneLite, gx: number, gy: number) {
  const dx = gx - zone.posX;
  const dy = gy - zone.posY;
  // Rotation inverse
  const cos = Math.cos(rad(-zone.rotation));
  const sin = Math.sin(rad(-zone.rotation));
  return {
    x: dx * cos - dy * sin,
    y: dx * sin + dy * cos,
  };
}

/**
 * Convertit une rotation globale (table en repère monde) en rotation locale à la zone.
 * Inverse : `globalRot = localRot + zoneRot (mod 360)`.
 */
export function localRotFromGlobal(globalRotDeg: number, zoneRot: ZoneRot) {
  const v = ((globalRotDeg - zoneRot) % 360 + 360) % 360;
  return v;
}

export function globalRotFromLocal(localRotDeg: number, zoneRot: ZoneRot) {
  const v = ((localRotDeg + zoneRot) % 360 + 360) % 360;
  return v;
}

/**
 * Retourne true si le point local `(lx, ly)` est à l'intérieur du rectangle de la zone.
 */
function isInsideLocal(zone: AllViewZoneLite, lx: number, ly: number) {
  return lx >= 0 && lx <= zone.canvasWidth && ly >= 0 && ly <= zone.canvasHeight;
}

/**
 * Cherche la zone qui contient le point global `(gx, gy)`.
 * Stratégie : on prend la zone avec le plus grand `orderIndex` parmi celles qui contiennent le point.
 * `preferredZoneId` (zone courante de la table) est priorisé en cas d'égalité visuelle.
 * Retourne `null` si aucune zone ne contient le point.
 */
export function findZoneAtPoint(
  zones: AllViewZoneLite[],
  gx: number,
  gy: number,
  preferredZoneId?: string | null,
): AllViewZoneLite | null {
  const matches = zones.filter(z => {
    const { x, y } = localFromGlobal(z, gx, gy);
    return isInsideLocal(z, x, y);
  });
  if (matches.length === 0) return null;
  if (preferredZoneId) {
    const pref = matches.find(z => z.id === preferredZoneId);
    if (pref) return pref;
  }
  return matches.reduce((best, z) => (z.orderIndex > best.orderIndex ? z : best), matches[0]);
}

/**
 * Borne un point local à l'intérieur du rectangle d'une zone (utilisé pour empêcher
 * qu'une table sorte de sa zone d'origine quand on l'a relâchée hors de toute zone).
 */
export function clampLocalToZone(
  zone: AllViewZoneLite,
  lx: number,
  ly: number,
  width: number,
  height: number,
) {
  return {
    x: Math.max(0, Math.min(zone.canvasWidth - width, lx)),
    y: Math.max(0, Math.min(zone.canvasHeight - height, ly)),
  };
}
